import anime from 'animejs';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { createFocusTrap } from 'focus-trap';

import mobileQuickSearch from '@/lib/mobile-quick-search';

const sel = {
  overlay: '[data-overlay]',
  listItem: '[data-list-item]',
  item: '[data-item]',
  allLinks: '[data-all-links]',
  main: '[data-main]',
  primary: '[data-primary-container]',
  footer: '[data-footer]',
  close: '[data-close-drawer]',
  logo: '.drawer-menu__logo',
  // Cross border
  form: '.drawer-menu__form',
  localeInput: '[data-locale-input]',
  currencyInput: '[data-currency-input]',
  // Quick search
  quickSearch: '[data-quick-search]',
};

const classes = {
  active: 'active',
  visible: 'visible',
  countrySelector: 'drawer-menu__list--country-selector',
};

const transitionIn = targets =>
  anime({
    targets,
    translateX: [40, 0],
    opacity: [0, 1],
    easing: 'cubicBezier(.5, .05, .1, .3)',
    duration: 120,
    delay: anime.stagger(60, { start: 60 }),
    complete: function () {
      targets.forEach(el => el.style.removeProperty('transform'));
    },
  });

// Extra space we add to the height of the inner container
const formatHeight = h => h + 8 + 'px';

const menu = node => {
  // Entire links container
  let primaryDepth = 0;
  // The individual link list the merchant selected
  let linksDepth = 0;

  let scrollPosition = 0;

  const focusTrap = createFocusTrap(node, { allowOutsideClick: true });

  const overlay = node.querySelector(sel.overlay);
  overlay.addEventListener('click', close);

  const quickSearchEl = node.querySelector(sel.quickSearch);
  const quickSearch = mobileQuickSearch(quickSearchEl);

  const searchLink = node.querySelector('[data-search]');
  searchLink.addEventListener('click', openSearch);

  // Element that holds all links, primary and secondary
  const everything = node.querySelector(sel.allLinks);

  // This is the element that holds the one we move left and right (primary)
  // We also need to assign its height initially so we get smooth transitions
  const main = node.querySelector(sel.main);

  // Element that holds all the primary links and moves left and right
  const primary = node.querySelector(sel.primary);

  // Cross border
  const form = node.querySelector(sel.form);
  const localeInput = node.querySelector(sel.localeInput);
  const currencyInput = node.querySelector(sel.currencyInput);

  const closeBtn = node.querySelector(sel.close);
  closeBtn.addEventListener('click', close);

  // Every individual menu item
  const items = node.querySelectorAll(sel.item);
  items.forEach(item => item.addEventListener('click', handleItem));

  function openSearch(e) {
    e.preventDefault();
    e.target.setAttribute('aria-expanded', true);
    quickSearch.open();
  }

  function handleItem(e) {
    e.preventDefault();
    const { item } = e.currentTarget.dataset;

    switch (item) {
      // Standard link that goes to a different url
      case 'link':
        close();
        window.location = e.currentTarget.href;
        break;
      // Element that will navigate to child navigation list
      case 'parent':
        clickParent(e);
        break;
      // Element that will navigate back up the tree
      case 'back':
        clickBack(e);
        break;
      // Account, currency, and language link at the bottom
      case 'secondary':
        handleSecondaryLink(e);
        break;
      // Back link within 'Currency' or 'Language'
      case 'secondaryHeading':
        handleSecondaryHeading(e);
        break;
      // Individual language
      case 'locale':
        handleLanguage(e);
        break;
      // Individual currency
      case 'currency':
        handleCurrency(e);
        break;
    }
  }

  function open() {
    node.classList.add(classes.active);
    setTimeout(() => {
      focusTrap.activate();
      node.classList.add(classes.visible);

      disableBodyScroll(node, {
        hideBodyOverflow: true,
        allowTouchMove: el => {
          while (el && el !== document.body && el.id !== 'main-content') {
            if (el.getAttribute('data-scroll-lock-ignore') !== null) {
              return true;
            }
            el = el.parentNode;
          }
        },
      });

      scrollPosition = window.pageYOffset;
      document.body.style.top = `-${scrollPosition}px`;
      document.body.classList.add('scroll-lock');

      const elements = node.querySelectorAll(
        `${sel.primary} > ${sel.listItem}, ${sel.footer} > li, ${sel.footer} > form > li`
      );

      if (primaryDepth === 0 && linksDepth === 0) {
        main.style.height = formatHeight(primary.offsetHeight);
        transitionIn(elements);
      }
    }, 50);
  }

  function close(e) {
    e && e.preventDefault();
    focusTrap.deactivate();

    node.classList.remove(classes.visible);
    setTimeout(() => {
      node.classList.remove(classes.active);
      enableBodyScroll(node);
      document.body.classList.remove('scroll-lock');
      document.body.style.top = '';
      window.scrollTo(0, scrollPosition);
    }, 350);
  }

  function clickParent(e) {
    e.preventDefault();
    const parentLink = e.currentTarget;
    parentLink.ariaExpanded = 'true';

    const childMenu = parentLink.nextElementSibling;

    childMenu.classList.add(classes.visible);
    main.style.height = formatHeight(childMenu.offsetHeight);

    // childMenu.querySelector('a').focus();

    const elements = childMenu.querySelectorAll(`:scope > ${sel.listItem}`);
    transitionIn(elements);

    navigate((linksDepth += 1));
  }

  function navigate(depth) {
    linksDepth = depth;
    primary.setAttribute('data-depth', depth);
  }

  function navigatePrimary(depth) {
    primaryDepth = depth;
    everything.setAttribute('data-depth', depth);
  }

  function clickBack(e) {
    e.preventDefault();

    const menuBefore = e.currentTarget.closest(sel.listItem).closest('ul');
    main.style.height = formatHeight(menuBefore.offsetHeight);

    const parent = e.currentTarget.closest('ul');
    parent.classList.remove(classes.visible);

    const parentLink = parent.previousElementSibling;
    parentLink.ariaExpanded = 'false';

    navigate((linksDepth -= 1));
  }

  function handleSecondaryLink(e) {
    e.preventDefault();
    navigatePrimary(1);
    const childMenu = e.currentTarget.nextElementSibling;
    childMenu.classList.add(classes.visible);

    const elements = childMenu.querySelectorAll(`:scope > ${sel.listItem}`);

    // Due to the country selectors length it cannot be transitioned in
    if (!childMenu.classList.contains(classes.countrySelector)) {
      transitionIn(elements);
    }
  }

  function handleSecondaryHeading(e) {
    e.preventDefault();
    navigatePrimary(0);

    const parent = e.currentTarget.closest('ul');
    parent.classList.remove(classes.visible);
  }

  function handleCrossBorder(e, input) {
    const { value } = e.currentTarget.dataset;
    input.value = value;
    close();
    form.submit();
  }

  function handleKeyboard(e) {
    if (!node.classList.contains(classes.visible)) return;

    if (e.key == 'Escape' || (e.keyCode === 27 && drawerOpen)) {
      close();
    }
  }

  const handleLanguage = e => handleCrossBorder(e, localeInput);
  const handleCurrency = e => handleCrossBorder(e, currencyInput);

  window.addEventListener('keydown', handleKeyboard);

  function destroy() {
    overlay.removeEventListener('click', close);
    closeBtn.removeEventListener('click', close);
    searchLink.removeEventListener('click', openSearch);
    items.forEach(item => item.removeEventListener('click', handleItem));
    enableBodyScroll(node);
    document.body.classList.remove('scroll-lock');
    document.body.style.top = '';
    window.scrollTo(0, scrollPosition);
    window.removeEventListener('keydown', handleKeyboard);
  }

  return { close, destroy, open };
};

export default menu;
